import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, orderBy, limit, onSnapshot, where } from 'firebase/firestore';
import { db } from '../firebase';
import { MasterTransaction } from '../types';
import { useFirebase } from '../contexts/FirebaseContext';
import { useLocation } from 'react-router-dom';
import { useStoreLocation } from '../contexts/LocationContext';
import { Search, Calendar, Filter, X, ChevronDown, ArrowUpRight, ArrowDownRight, BookOpen, BarChart3, Database, AlertCircle, Users, MapPin, Layers } from 'lucide-react';
import { Dispute } from '../services/disputeService';
import { getPendingDisputes } from '../services/disputeApprovalService';
import { AuditReconciliationTab } from './AuditReconciliationTab';
import { EmployeeFinancialOverview } from './EmployeeFinancialOverview';
import { SystemAuditLogs } from './SystemAuditLogs';
import { DisputeForm } from './DisputeForm';
import { getSettings, TenantSettings } from '../services/settingsService';
import { getStaff, Staff } from '../services/staffService';
import { SubscriptionGate } from './SubscriptionGate';
import { parseTransactionDate, matchesSource, getLocalDate } from '../lib/utils';
import { isSubmitted, isAllocated, isRejected as isRejectedHelper, isPendingFinalization, filterTransactionsByLocation } from '../lib/transactionUtils';
import { ReconciliationSummaryBoxes } from './ReconciliationSummaryBoxes';
import { getRelatedTransactionIds } from '../lib/transactionUtils';

export const TransactionAuditViewer: React.FC = () => {
  const { tenantId } = useFirebase();
  const location = useLocation();
  const { activeLocation, activeLocationId, isConsolidated } = useStoreLocation();
  const [transactions, setTransactions] = useState<MasterTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'audit' | 'reconciliation' | 'earnings' | 'system'>('audit');
  const [pendingDisputes, setPendingDisputes] = useState<(Dispute & { id: string })[]>([]);
  const [allocationMethod, setAllocationMethod] = useState<string | null>(null);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    const fetchDisputes = async () => {
      const disputes = await getPendingDisputes(tenantId);
      setPendingDisputes(disputes || []);
    };
    const fetchStaffData = async () => {
      try {
        const list = await getStaff(tenantId);
        setStaffList(list || []);
      } catch (e) {
        console.error('Error fetching staff for audit viewer:', e);
      }
    };
    fetchDisputes();
    fetchStaffData();
  }, [tenantId]);
  const getTodayString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [tenantTimezone, setTenantTimezone] = useState('UTC');
  const [startDate, setStartDate] = useState(() => getLocalDate('UTC'));
  const [endDate, setEndDate] = useState(() => getLocalDate('UTC'));
  const [isReady, setIsReady] = useState(false);

  const clearDateFilters = () => {
    const todayStr = getLocalDate(tenantTimezone);
    setStartDate(todayStr);
    setEndDate(todayStr);
  };
  const [sourceFilter, setSourceFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [correctingTx, setCorrectingTx] = useState<MasterTransaction | null>(null);

  // 1. Initialize settings, URL parameters, and default dates
  useEffect(() => {
    if (!tenantId) return;

    const init = async () => {
      try {
        const settings = await getSettings(tenantId);
        setAllocationMethod(settings?.allocationMethod || null);
        const timeZone = settings?.timezone || 'UTC';
        setTenantTimezone(timeZone);

        const params = new URLSearchParams(location.search);
        const dateParam = params.get('date');

        if (dateParam) {
          setStartDate(dateParam);
          setEndDate(dateParam);
        } else {
          const todayStr = getLocalDate(timeZone);
          setStartDate(todayStr);
          setEndDate(todayStr);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setIsReady(true);
      }
    };
    init();
  }, [tenantId, location.search]);

  // 2. Subscribe to Firestore with database-side date filters to keep query speeds blazing fast
  useEffect(() => {
    if (!tenantId || !isReady) return;

    const txRef = collection(db, 'tenants', tenantId, 'master_transactions');
    let q;

    if (startDate && endDate) {
      q = query(
        txRef,
        where('tipDate', '>=', startDate),
        where('tipDate', '<=', endDate)
      );
    } else if (startDate) {
      q = query(txRef, where('tipDate', '>=', startDate));
    } else if (endDate) {
      q = query(txRef, where('tipDate', '<=', endDate));
    } else {
      q = query(txRef);
    }

    setLoading(true);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));
      console.log('DEBUG: Loaded filtered transactions:', data.length);
      setTransactions(data);
      setLoading(false);
    }, (error) => {
      console.error('Error in transactions listener:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [tenantId, isReady, startDate, endDate]);

  const resolveStaffDetails = React.useCallback((tx: MasterTransaction) => {
    const matched = staffList.find(s => 
      (tx.staffId && s.id === tx.staffId) ||
      (s.email && tx.email && s.email.toLowerCase().trim() === tx.email.toLowerCase().trim()) ||
      (s.name && tx.staffName && s.name.toLowerCase().trim() === tx.staffName.toLowerCase().trim()) ||
      (tx.posId && s.posId && s.posId.trim() === tx.posId.trim())
    );
    return {
      displayName: tx.staffName || matched?.name || tx.email || 'Unknown Staff',
      posId: tx.posId || matched?.posId || null,
      payrollId: matched?.payrollId || null,
      staffId: tx.staffId || matched?.id || null,
      email: tx.email || matched?.email || null,
      role: matched?.role || null,
    };
  }, [staffList]);

  const filteredTransactions = React.useMemo(() => {
    const locationScopedTransactions = filterTransactionsByLocation(transactions, activeLocationId);
    const baseFiltered = locationScopedTransactions.filter(t => {
      const staff = resolveStaffDetails(t);
      const term = searchTerm.toLowerCase().trim();
      const matchesSearch = !searchTerm || 
        t.staffName?.toLowerCase().includes(term) ||
        staff.displayName?.toLowerCase().includes(term) ||
        t.email?.toLowerCase().includes(term) ||
        (staff.email && staff.email.toLowerCase().includes(term)) ||
        t.id.toLowerCase().includes(term) ||
        (t.posId && t.posId.toLowerCase().includes(term)) ||
        (staff.posId && staff.posId.toLowerCase().includes(term)) ||
        (staff.payrollId && staff.payrollId.toLowerCase().includes(term)) ||
        (staff.staffId && staff.staffId.toLowerCase().includes(term)) ||
        (t.source_id && t.source_id.toLowerCase().includes(term)) ||
        (t.parentTxId && t.parentTxId.toLowerCase().includes(term));
      
      let filterSource = sourceFilter;
      if (filterSource === 'SplitPool' && allocationMethod === 'Split by Role') {
        filterSource = 'Split by Role';
      }
      const matchesSourceResult = matchesSource(t.source, filterSource);
      
      const transactionDate = parseTransactionDate(t.tipDate || t.timestamp);

      const matchesDate = 
        (!startDate || transactionDate >= startDate) &&
        (!endDate || transactionDate <= endDate);
  
      const isDispute = t.source === 'Disputes';
      const normalizedStatus = (t.submissionStatus || (t as any).status || '').toLowerCase().trim();
      const isApproved = normalizedStatus === 'approved' || normalizedStatus === 'finalized';
      const isRejected = isRejectedHelper(t);

      const matchesStatus = statusFilter === 'All' || 
        (statusFilter === 'Finalized' && (isAllocated(t, locationScopedTransactions) || isApproved)) ||
        (statusFilter === 'Pending' && !isAllocated(t, locationScopedTransactions) && !isApproved && !isRejected);
  
      return matchesSearch && matchesSourceResult && matchesDate && matchesStatus && !isDispute;
    });
    
    // Expand to all related transactions
    const relatedIds = getRelatedTransactionIds(locationScopedTransactions, baseFiltered.map(t => t.id));
    const filtered = locationScopedTransactions.filter(t => relatedIds.has(t.id));
    
    // Sort by latest timestamp
    filtered.sort((a, b) => {
      const timeA = (a.auditTimestamp || a.timestamp)?.toDate?.()?.getTime() || 0;
      const timeB = (b.auditTimestamp || b.timestamp)?.toDate?.()?.getTime() || 0;
      return timeB - timeA;
    });
    
    // DEBUG
    console.log('DEBUG: Filtered count:', filtered.length, 'Total Transactions:', transactions.length);
    
    return filtered;
  }, [transactions, searchTerm, startDate, endDate, sourceFilter, statusFilter, allocationMethod]);

  const totalSubmittedAmount = useMemo(() => {
    const sum = filteredTransactions.reduce((sum, t) => 
      isSubmitted(t, transactions) && t.source !== 'Calculated'
        ? sum + (t.submittedAmount || t.amount || 0) 
        : sum, 0);
    return Math.round(sum * 100) / 100;
  }, [filteredTransactions, transactions]);
  const totalAllocatedAmount = useMemo(() => {
    const sum = filteredTransactions.reduce((sum, t) => 
      isAllocated(t, transactions) && (t.amount || t.allocatedAmount) 
        ? sum + (t.allocatedAmount || t.amount || 0) 
        : sum, 0);
    return Math.round(sum * 100) / 100;
  }, [filteredTransactions, transactions]);

  const totalPendingFinalization = useMemo(() => filteredTransactions
    .filter(isPendingFinalization)
    .reduce((acc, tx) => acc + (tx.amount || 0), 0), [filteredTransactions]);

  const totalAllocatedWithPending = totalAllocatedAmount + totalPendingFinalization;

  const clearFilters = () => {
    setSearchTerm('');
    const todayStr = getLocalDate(tenantTimezone);
    setStartDate(todayStr);
    setEndDate(todayStr);
    setSourceFilter('All');
    setStatusFilter('All');
  };

  const jumpToParent = (parentId: string) => {
    setSearchTerm(parentId);
    setActiveTab('audit');
  };

  if (loading || !isReady) return (
    <div className="flex items-center justify-center p-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <span className="ml-3 text-gray-500 font-medium">Loading Audit Trail...</span>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-2xl font-black text-gray-900 tracking-tight uppercase">Master Transaction Detail</h2>
            {isConsolidated ? (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-800 font-bold border border-blue-200">
                <Layers size={12} />
                All Stores (Consolidated)
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-800 font-bold border border-slate-200">
                <MapPin size={12} className="text-blue-600" />
                {activeLocation?.name || 'Selected Store'}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 font-medium">Internal ledger visibility and data lineage tracking.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto">
          <div className="flex bg-gray-100 rounded-lg p-1 overflow-x-auto max-w-full whitespace-nowrap scrollbar-none">
            <button 
              onClick={() => setActiveTab('audit')}
              className={`flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-bold transition-colors ${activeTab === 'audit' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              <BookOpen size={14} />
              Audit Trail
            </button>
            <button 
              onClick={() => setActiveTab('reconciliation')}
              className={`flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-bold transition-colors ${activeTab === 'reconciliation' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              <BarChart3 size={14} />
              Reconciliation
            </button>
            <button 
              onClick={() => setActiveTab('earnings')}
              className={`flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-bold transition-colors ${activeTab === 'earnings' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              <Users size={14} />
              Employee Earnings
            </button>
            <button 
              onClick={() => setActiveTab('system')}
              className={`flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-bold transition-colors ${activeTab === 'system' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              <Database size={14} />
              System
            </button>
          </div>
          
          <button 
            onClick={clearFilters}
            className="flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold text-gray-400 hover:text-gray-600 border border-gray-100 sm:border-0 rounded-lg bg-gray-50/50 sm:bg-transparent transition-colors py-2"
          >
            <X size={14} />
            Reset
          </button>
        </div>
      </div>

      <div className="card p-3.5 sm:p-6 bg-white border border-gray-100 shadow-sm space-y-4 sm:space-y-6 !overflow-visible">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          <div className="relative col-span-1 lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search staff, ID, or Parent Link..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
          </div>
          
          <div className="col-span-1 sm:col-span-2 lg:col-span-2 grid grid-cols-2 gap-2">
            <div className="relative min-w-0">
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 hidden sm:block" size={14} />
              <input 
                type="date" 
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full pl-2 sm:pl-9 pr-1 sm:pr-4 py-1.5 sm:py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[11px] sm:text-xs focus:ring-2 focus:ring-blue-500 outline-none transition-all font-bold text-gray-700 min-w-0 h-[36px] sm:h-[40px]"
              />
              <span className="absolute -top-2 left-2 px-1 bg-white text-[8px] font-black text-gray-400 uppercase tracking-widest">From</span>
            </div>

            <div className="relative min-w-0">
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 hidden sm:block" size={14} />
              <input 
                type="date" 
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full pl-2 sm:pl-9 pr-1 sm:pr-4 py-1.5 sm:py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[11px] sm:text-xs focus:ring-2 focus:ring-blue-500 outline-none transition-all font-bold text-gray-700 min-w-0 h-[36px] sm:h-[40px]"
              />
              <span className="absolute -top-2 left-2 px-1 bg-white text-[8px] font-black text-gray-400 uppercase tracking-widest">To</span>
              {(startDate || endDate) && (
                <button 
                  onClick={clearDateFilters} 
                  className="absolute -top-2 right-2 px-1 text-[8px] font-black uppercase text-blue-600 hover:text-blue-800 bg-white"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <select 
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 outline-none transition-all appearance-none cursor-pointer font-bold"
            >
              <option value="All">All Sources</option>
              <option value="POS">POS Only</option>
              <option value="Cash">Cash Only</option>
              <option value="Jar">Jar Only</option>
              {allocationMethod && <option value={allocationMethod === 'Split by Role' ? 'SplitPool' : allocationMethod}>{allocationMethod} Only</option>}
              <option value="Disputes">Disputes Only</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <span className="absolute -top-2 left-2 px-1 bg-white text-[8px] font-black text-gray-400 uppercase tracking-widest">Source</span>
          </div>

          <div className="relative">
            <select 
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 outline-none transition-all appearance-none cursor-pointer font-bold"
            >
              <option value="All">All Statuses</option>
              <option value="Finalized">Finalized Only</option>
              <option value="Pending">Pending Only</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <span className="absolute -top-2 left-2 px-1 bg-white text-[8px] font-black text-gray-400 uppercase tracking-widest">Allocation</span>
          </div>
        </div>

        {activeTab === 'audit' ? (
            <div className="max-w-full max-h-[70vh] overflow-auto pb-4 border border-gray-100 rounded-lg">

              <ReconciliationSummaryBoxes transactions={filteredTransactions} pendingDisputes={pendingDisputes} />
              <div className="px-4 py-2 bg-gray-50 text-[10px] text-gray-500 font-medium border-b border-gray-100">
                Showing latest 100 transactions
              </div>
              {/* Mobile Card Layout */}
              <div className="block md:hidden space-y-4 px-4 py-2">
                {filteredTransactions.length > 0 ? (
                  <>
                    {filteredTransactions.map(t => {
                      const staff = resolveStaffDetails(t);
                      return (
                      <div key={t.id} className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm space-y-3 relative hover:border-blue-100 active:bg-gray-50/50 transition-all">
                        {/* Status/Badge bar */}
                        <div className="flex justify-between items-center bg-gray-50/80 -mx-4 -mt-4 px-4 py-2 rounded-t-xl border-b border-gray-100">
                          <span className="font-mono text-xs font-semibold text-gray-600">{t.tipDate}</span>
                          <div className="flex gap-1.5">
                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border ${
                              t.submissionStatus === 'Finalized' || t.submissionStatus === 'Approved' 
                              ? 'bg-sky-50 text-sky-700 border-sky-100'
                              : t.submissionStatus === 'Rejected'
                              ? 'bg-red-50 text-red-700 border-red-100'
                              : 'bg-amber-50 text-amber-700 border-amber-100'
                            }`}>
                              {t.submissionStatus}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border ${
                              t.allocationStatus === 'Finalized' 
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                              : 'bg-gray-50 text-gray-400 border-gray-100'
                            }`}>
                              {t.allocationStatus}
                            </span>
                          </div>
                        </div>

                        {/* Top Context: User and basic attributes */}
                        <div className="flex justify-between items-start gap-2 pt-1">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-extrabold text-sm text-gray-900 leading-tight">{staff.displayName}</p>
                              {staff.role && (
                                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[9px] font-bold">
                                  {staff.role}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                              {staff.posId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[9px] font-mono font-bold uppercase">
                                  POS ID: {staff.posId}
                                </span>
                              )}
                              {staff.payrollId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[9px] font-mono font-bold uppercase">
                                  Payroll ID: {staff.payrollId}
                                </span>
                              )}
                              {staff.staffId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] font-mono font-bold" title={`Staff ID: ${staff.staffId}`}>
                                  Staff ID: {staff.staffId.slice(0, 8)}
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-gray-400 font-medium break-all">{staff.email || '-'}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider ${
                              t.source === 'POS' ? 'bg-indigo-50 text-indigo-600' :
                              t.source === 'Cash' ? 'bg-blue-50 text-blue-600' :
                              'bg-purple-50 text-purple-600'
                            }`}>
                              {t.source}
                            </span>
                            {t.auditVerified ? (
                              <span className="text-emerald-500 font-bold text-xs flex items-center gap-0.5">
                                <span className="text-sm">✓</span> Verified
                              </span>
                            ) : (
                              <span className="text-gray-300 font-medium text-[10px] italic">Unverified</span>
                            )}
                          </div>
                        </div>

                        {/* Middle financials detail in box layout */}
                        <div className="grid grid-cols-2 gap-3 bg-gray-50/70 p-3 rounded-xl border border-gray-100 text-xs">
                          <div>
                            <span className="block text-[8px] uppercase font-black text-gray-400 tracking-wider mb-0.5">Submitted</span>
                            <span className="text-gray-900 font-black text-sm">
                              {((isSubmitted(t, transactions) || t.source === 'Disputes') && t.source !== 'Calculated') 
                                ? `$${(t.submittedAmount || t.amount || 0).toFixed(2)}` 
                                : <span className="text-gray-300">-</span>}
                            </span>
                          </div>
                          <div>
                            <span className="block text-[8px] uppercase font-black text-gray-400 tracking-wider mb-0.5">Allocated</span>
                            <span className="text-emerald-600 font-black text-sm">
                              {isAllocated(t, transactions) && t.source !== 'Disputes' && (t.amount || t.allocatedAmount) ? (
                                `$${(t.allocatedAmount || t.amount || 0).toFixed(2)}`
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </span>
                          </div>
                        </div>

                        {/* Traceability: lineage and reference */}
                        <div className="text-[10px] space-y-1 text-gray-400 pt-1 border-t border-gray-100/40">
                          {console.log('DEBUG [TransactionAuditViewer] Transaction:', t)}
                          <div className="flex items-center justify-between border-b border-gray-50 pb-1">
                            <span className="text-gray-400 font-bold">Record ID:</span>
                            <span className="font-mono text-gray-600 select-all">{t.id}</span>
                          </div>
                          <div className="flex items-center justify-between border-b border-gray-50 pb-1">
                            <span className="text-gray-400 font-bold">POS ID:</span>
                            <span className="font-mono text-gray-600 select-all">{staff.posId || 'Not Assigned'}</span>
                          </div>
                          {staff.payrollId && (
                            <div className="flex items-center justify-between border-b border-gray-50 pb-1">
                              <span className="text-gray-400 font-bold">Payroll ID:</span>
                              <span className="font-mono text-purple-700 font-bold select-all">{staff.payrollId}</span>
                            </div>
                          )}
                          {staff.staffId && (
                            <div className="flex items-center justify-between border-b border-gray-50 pb-1">
                              <span className="text-gray-400 font-bold">Staff ID:</span>
                              <span className="font-mono text-slate-700 select-all" title={staff.staffId}>{staff.staffId.slice(0, 12)}</span>
                            </div>
                          )}
                          {(t.parentTxId || t.relatedTransactionId) && (
                            <div className="flex items-center justify-between border-b border-gray-50 pb-1">
                              <span className="text-gray-400 font-bold">Parent Connection:</span>
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-[9px] text-blue-600 bg-blue-50 px-1 py-0.5 rounded">
                                  {(t.parentTxId || t.relatedTransactionId)?.substring(0, 10)}...
                                </span>
                                <button 
                                  onClick={() => jumpToParent(t.parentTxId || t.relatedTransactionId || '')}
                                  className="p-1 hover:bg-blue-600 hover:text-white rounded text-blue-500 transition-colors"
                                  title="Trace Parent"
                                >
                                  <Search size={10} />
                                </button>
                              </div>
                            </div>
                          )}
                          {t.allocationMethod && (
                            <div className="flex items-center justify-between border-b border-gray-50 pb-1">
                              <span className="text-gray-400 font-bold">Method:</span>
                              <span className="text-gray-700 font-semibold">{t.allocationMethod}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 font-bold">Authorized By:</span>
                            <div className="text-right">
                              <span className="text-gray-700 font-bold block">{t.auditActor || 'system'}</span>
                              <span className="text-[8px] text-gray-400">
                                {(t.auditTimestamp || t.timestamp)?.toDate?.()?.toLocaleString() || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Action buttons section */}
                        <div className="pt-2 border-t border-gray-100 flex justify-end">
                          <button 
                            onClick={() => setCorrectingTx(t)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200/60 rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                            title="Dispute Correction"
                          >
                            <AlertCircle size={13} />
                            Log Dispute
                          </button>
                        </div>
                      </div>
                    );
                    })}

                    {/* Mobile Totals Box */}
                    <div className="p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-xs space-y-2">
                      <p className="text-xs font-black uppercase text-gray-600 tracking-wider">Filtered Audit Totals</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="p-2.5 bg-white rounded-xl border border-gray-100/50">
                          <span className="block text-[8px] text-gray-400 font-black uppercase mb-0.5">Total Submitted</span>
                          <span className="text-gray-900 font-black text-sm">${totalSubmittedAmount.toFixed(2)}</span>
                        </div>
                        <div className="p-2.5 bg-white rounded-xl border border-gray-100/50">
                          <span className="block text-[8px] text-gray-400 font-black uppercase mb-0.5">Total Allocated</span>
                          <span className="text-emerald-700 font-black text-sm">${totalAllocatedAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-12 text-center text-gray-400 font-medium italic border-2 border-dashed border-gray-100 rounded-2xl">
                    No transactions found matching criteria.
                  </div>
                )}
              </div>

              {/* Desktop/Tablet Table Layout */}
              <table className="hidden md:table w-full text-xs text-left border-collapse min-w-[1100px]">
                <thead>
                  <tr className="bg-white border-y border-gray-100 sticky top-0 z-10">
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest">Service Date</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest">Staff Name</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest text-right">Submitted Amount</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest text-right">Allocated</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest text-center">Source</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest text-center">Verified</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest">ID Reference</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest">Lineage Link</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest">Methodology</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest">Audit Actor</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest text-center">Submission</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest text-center">Allocation</th>
                    <th className="p-4 font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredTransactions.length > 0 ? (
                    <>
                      {filteredTransactions.map(t => {
                        const staff = resolveStaffDetails(t);
                        return (
                        <tr key={t.id} className="hover:bg-gray-50 transition-colors group">
                          <td className="p-4 font-mono font-medium text-gray-600">{t.tipDate}</td>
                          <td className="p-4">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="font-bold text-gray-900">{staff.displayName}</p>
                                {staff.role && (
                                  <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[8px] font-bold">
                                    {staff.role}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-1 mt-1">
                                {staff.posId && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[9px] font-mono font-bold uppercase">
                                    POS: {staff.posId}
                                  </span>
                                )}
                                {staff.payrollId && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[9px] font-mono font-bold uppercase">
                                    PAY: {staff.payrollId}
                                  </span>
                                )}
                                {staff.staffId && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] font-mono font-bold" title={`Staff ID: ${staff.staffId}`}>
                                    ID: {staff.staffId.slice(0, 8)}
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-gray-400 mt-0.5">{staff.email || '-'}</p>
                            </div>
                          </td>
                          <td className="p-4 text-right font-black text-gray-600">
                            {( (isSubmitted(t, transactions) || t.source === 'Disputes') && t.source !== 'Calculated') 
                              ? `$${(t.submittedAmount || t.amount || 0).toFixed(2)}` 
                              : '-'}
                          </td>
                          <td className="p-4 text-right">
                            {isAllocated(t, transactions) && t.source !== 'Disputes' && (t.amount || t.allocatedAmount) ? (
                              <span className="font-black text-emerald-600">${(t.allocatedAmount || t.amount || 0).toFixed(2)}</span>
                            ) : (
                              <span className="text-gray-300 italic">-</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider ${
                              t.source === 'POS' ? 'bg-indigo-50 text-indigo-600' :
                              t.source === 'Cash' ? 'bg-blue-50 text-blue-600' :
                              'bg-purple-50 text-purple-600'
                            }`}>
                              {t.source}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            {t.auditVerified ? (
                              <span className="text-emerald-500 text-lg">✓</span>
                            ) : (
                              <span className="text-gray-300 text-lg opacity-20">○</span>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-0.5">
                              <span className="font-mono text-[9px] text-gray-400 select-all">{t.id}</span>
                              {staff.posId && (
                                <span className="font-mono text-[9px] text-blue-700 font-bold">POS: {staff.posId}</span>
                              )}
                              {staff.payrollId && (
                                <span className="font-mono text-[9px] text-purple-700 font-bold">PAY: {staff.payrollId}</span>
                              )}
                              <span className={`text-[8px] font-black uppercase tracking-tighter mt-1 flex items-center gap-1 ${
                                t.source === 'Disputes' ? 'text-red-500' :
                                t.type === 'Allocated' ? 'text-blue-500' : 'text-amber-500'
                              }`}>
                                {t.source === 'Disputes' ? <AlertCircle size={10} /> : (t.type === 'Allocated' ? <ArrowDownRight size={10} /> : <ArrowUpRight size={10} />)}
                                {t.source === 'Disputes' ? 'Dispute' : (t.type === 'Allocated' ? 'Allocated' : 'Submitted')}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            {(t.parentTxId || t.relatedTransactionId) && (
                              <div className="flex flex-col gap-1">
                                <span className="text-[8px] text-gray-400 font-bold uppercase tracking-widest">Parent TX</span>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-[9px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 select-all">
                                    {(t.parentTxId || t.relatedTransactionId)?.substring(0, 12)}...
                                  </span>
                                  <button 
                                    onClick={() => jumpToParent(t.parentTxId || t.relatedTransactionId || '')}
                                    title="Find Original Transaction"
                                    className="p-1 hover:bg-blue-600 hover:text-white rounded transition-colors text-blue-400"
                                  >
                                    <Search size={12} />
                                  </button>
                                </div>
                              </div>
                            )}
                            {t.source_id && (
                               <div className="mt-1">
                                 <span className="text-[8px] font-bold text-gray-300 uppercase tracking-widest">Source ID: </span>
                                 <span className="text-[8px] text-gray-500 font-mono italic">{t.source_id}</span>
                               </div>
                            )}
                            {!t.parentTxId && !t.relatedTransactionId && (
                              <span className="text-[8px] text-gray-300 font-bold uppercase tracking-widest italic">Origin Record</span>
                            )}
                          </td>
                          <td className="p-4 text-center text-xs font-bold text-gray-500">
                             {t.allocationMethod || '-'}
                          </td>
                          <td className="p-4 text-gray-500">
                            <div className="flex flex-col">
                              <span className="font-bold text-gray-700">{t.auditActor || 'system'}</span>
                              <span className="text-[9px] text-gray-400">
                                {(t.auditTimestamp || t.timestamp)?.toDate?.()?.toLocaleString() || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <span className={`px-2 py-1 rounded-lg text-[9px] font-black border uppercase ${
                              t.submissionStatus === 'Finalized' || t.submissionStatus === 'Approved' 
                              ? 'bg-sky-50 text-sky-700 border-sky-100'
                              : t.submissionStatus === 'Rejected'
                              ? 'bg-red-50 text-red-700 border-red-100'
                              : 'bg-amber-50 text-amber-700 border-amber-100'
                            }`}>
                              {t.submissionStatus}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className={`px-2 py-1 rounded-lg text-[9px] font-black border uppercase ${
                              t.allocationStatus === 'Finalized' 
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                              : 'bg-gray-50 text-gray-400 border-gray-100'
                            }`}>
                              {t.allocationStatus}
                            </span>
                          </td>
                          <td className="p-4 text-right">
                            <button 
                              onClick={() => setCorrectingTx(t)}
                              className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                              title="Raise Adjustment Dispute"
                            >
                              <AlertCircle size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                      })}
                      <tr className="bg-gray-50/80">
                        <td colSpan={2} className="p-4 text-right font-black uppercase text-gray-700">Totals</td>
                        <td className="p-4 text-right font-black text-gray-900">${totalSubmittedAmount.toFixed(2)}</td>
                        <td className="p-4 text-right font-black text-emerald-700">${totalAllocatedWithPending.toFixed(2)}</td>
                        <td colSpan={8}></td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={12} className="p-20 text-center text-gray-400 font-medium italic">
                        No transactions found matching your audit criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
        ) : activeTab === 'reconciliation' ? (
          <AuditReconciliationTab transactions={filteredTransactions} allTransactions={transactions} startDate={startDate} endDate={endDate} />
        ) : activeTab === 'earnings' ? (
          <EmployeeFinancialOverview tenantId={tenantId!} startDate={startDate} endDate={endDate} sourceFilter={sourceFilter} />
        ) : (
          <div className="space-y-8">
             <div className="border-t pt-8">
              <SystemAuditLogs tenantId={tenantId!} />
              <SubscriptionGate featureName="Raw Data Exports" featureId="advanced_reporting">
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div className="card p-6 bg-white border border-gray-100 italic text-gray-400">
                      Raw Data Extract: POS Transactions (Placeholder)
                  </div>
                  <div className="card p-6 bg-white border border-gray-100 italic text-gray-400">
                      Raw Data Extract: Cash/Jar Transactions (Placeholder)
                  </div>
                </div>
              </SubscriptionGate>
            </div>
          </div>
        )}
      </div>

      {correctingTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-[2.5rem] shadow-2xl relative animate-in zoom-in-95 duration-300">
            <button 
              onClick={() => setCorrectingTx(null)}
              className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-full text-gray-400 z-10"
            >
              <X size={24} />
            </button>
            <div className="p-2 md:p-4">
              <DisputeForm tenantId={tenantId!} prefillTip={correctingTx} />
            </div>
            <div className="px-8 pb-8 flex justify-end">
                <button 
                  onClick={() => setCorrectingTx(null)}
                  className="px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 hover:text-gray-700 bg-gray-100 rounded-xl transition-all"
                >
                  Close Correction Interface
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
